import type React from 'react'
import { useState } from 'react'
import { ArrowUp, ChevronDown, ChevronUp, ExternalLink, Loader2 } from 'lucide-react'
import { formatDistance as formatDistanceUnits } from '../../lib/format-distance'

interface ResultRow {
  k: string
  v: string
  /**
   * Stable id of the underlying boundary. Used to drive the map's
   * hover-highlight feature-state. Optional so plain rows (e.g. fallback
   * states) don't need to fabricate one.
   */
  geoborder_id?: string | null
}

interface DistanceCallout {
  meters: number
  miles: number
  /** Null when bearing is undefined (point on boundary). The label still renders. */
  direction: string | null
  /** Bearing in degrees clockwise from north (0..360). Null mirrors `direction`. */
  bearing_degrees: number | null
  label: string
}

/**
 * Format a distance for display. Switch to feet when under 0.8 mi
 * (~4,200 ft) so coords right at the border show "324 ft · SSE" instead
 * of "0.06 mi · SSE". Meters drive the computation — miles is too
 * lossy for small distances.
 */
function formatDistance(d: DistanceCallout): string {
  return formatDistanceUnits(d.meters, d.miles)
}

/**
 * "Federal Land" status pinned to the bottom of the result panel.
 * Renders ONLY when the point is on federal land — private-land
 * coordinates show nothing there (the at-a-glance signal is the
 * presence of the row at all). On the homepage we surface it as a
 * link to the Explore page's Public Lands group (carrying the
 * current coord) so users can drill into the agency-specific
 * details. The pill uses the U.S. flag emoji 🇺🇸 directly — it
 * renders consistently across modern systems and signals the
 * federal nature at a glance better than an outlined flag icon.
 */
interface LandStatus {
  /**
   * Name of the closest federal layer that matched (e.g. "Tonto
   * National Forest"). Renders muted under the "Federal Land" pill
   * so users see WHICH federal designation triggered.
   */
  detail: string | null
  /**
   * Optional link target. When set, the row becomes a link. Used on
   * the homepage to deep-link into Explore's Public Lands group at
   * the same coordinate; null on Explore itself.
   */
  href?: string | null
}

interface ResultsPanelProps {
  rows: ResultRow[]
  distance: DistanceCallout | null
  landStatus?: LandStatus | null
  loading?: boolean
  error?: string | null
  /** Fires when the cursor enters / leaves a row with a `geoborder_id`. */
  onRowHover?: (geoborder_id: string | null) => void
  /**
   * Fires when a row with a `geoborder_id` is clicked. The page should
   * fit-bounds the map to that boundary's extent.
   */
  onRowClick?: (geoborder_id: string) => void
}

// Padding handled by inner content blocks (not on the aside itself)
// so the collapsed pill can be compact-py while the full panel keeps
// its breathable py-4.5. Otherwise toggling collapse would also
// change the aside's height awkwardly on desktop, where the
// conditional was previously cross-cutting.
const CONTAINER_CLASS = [
  // Mirror the other floating chrome's 12 px (left-3 / right-3 / top-3) so
  // the panel feels visually anchored at the same edge inset.
  'absolute right-3 bottom-3 z-20',
  'w-[clamp(280px,90vw,340px)]',
  'rounded-[var(--radius-lg)]',
  'bg-[var(--color-elev-strong)] backdrop-blur-[14px]',
  'border border-[var(--color-border)]',
  'text-[var(--color-text)]',
  'shadow-[var(--shadow-results)]',
  'transition-colors duration-200',
].join(' ')

/**
 * Bottom-of-panel "Federal Land" status row. Only renders when the
 * point is on federal land; otherwise the caller passes null and
 * nothing shows up there. The pill is the literal U.S. flag emoji
 * 🇺🇸 — the right visual for "this is federal land". When
 * `status.href` is set, the row is a link to deep-link into
 * Explore's Public Lands group at the current coord.
 */
function LandStatusRow({ status }: { status: LandStatus }) {
  const pill = (
    <span
      className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[var(--color-surface)] text-[14px] leading-none"
      aria-hidden
    >
      🇺🇸
    </span>
  )

  const content = (
    <span className="flex flex-col leading-tight">
      <span className="flex items-center gap-1.5 font-[var(--font-mono)] text-[13px]">
        Federal Land
        {status.href ? (
          <ExternalLink
            size={11}
            aria-hidden
            className="text-[var(--color-accent)]"
          />
        ) : null}
      </span>
      {status.detail ? (
        <span className="text-[11.5px] text-[var(--color-muted)] truncate">
          {status.detail}
        </span>
      ) : null}
    </span>
  )

  const row = (
    <div className="flex items-center gap-2.5">
      {pill}
      {content}
    </div>
  )

  if (!status.href) return <div className="mt-1">{row}</div>
  return (
    <a
      href={status.href}
      className="mt-1 block -mx-1.5 rounded-sm px-1.5 py-1 hover:bg-[var(--color-accent-soft)] transition-colors"
      aria-label="Federal Land — open Explore Public Lands at this coordinate"
    >
      {row}
    </a>
  )
}

export function ResultsPanel({
  rows,
  distance,
  landStatus = null,
  loading = false,
  error = null,
  onRowHover,
  onRowClick,
}: ResultsPanelProps) {
  // Mobile users get a collapse toggle so the panel doesn't dominate the
  // viewport — the rows otherwise eat most of the map on small screens.
  // Default collapsed below sm; the distance-to-nearest-border callout
  // stays visible in the collapsed state since it's the most actionable
  // single line of context. Toggle hidden at sm+ where the floating
  // panel only takes ~340 px and the map has room alongside it.
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < 640
  })

  return (
    <aside
      aria-label="Result for current coordinate"
      className={CONTAINER_CLASS}
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? 'Expand result panel' : 'Collapse result panel'}
        aria-expanded={!collapsed}
        className={[
          // Top-right of the panel. Accent-soft background + accent icon
          // so the expand affordance reads as a real control rather than
          // muted chrome. Visible only below sm — desktops never collapse.
          // When collapsed, vertically center against the single distance
          // row so the chevron lines up symmetrically with the label
          // instead of sitting slightly above. When expanded, top-2 keeps
          // it pinned near the top of a multi-row card.
          'sm:hidden absolute right-2 z-20',
          collapsed ? 'top-1/2 -translate-y-1/2' : 'top-2',
          'inline-flex items-center justify-center w-8 h-8 rounded-md cursor-pointer',
          'bg-[var(--color-accent-soft)] text-[var(--color-accent)]',
          'border border-[var(--color-accent)]/30',
          'hover:bg-[var(--color-accent-soft)]/80 transition-colors',
        ].join(' ')}
      >
        {collapsed ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
      </button>
      {/* Collapsed pill — mobile only, and only when `collapsed`. CSS-
          driven visibility (rather than ternary render) so desktop's
          full panel is always present regardless of any stale collapsed
          state from a prior smaller viewport. */}
      <div
        className={[
          'px-5 py-2.5 pr-12 sm:hidden',
          collapsed ? 'block' : 'hidden',
        ].join(' ')}
      >
        {distance ? (
          <div className="flex items-center gap-2.5 font-[var(--font-mono)] text-[13px] tabular-nums">
            <span
              aria-hidden
              className={[
                'inline-flex items-center justify-center w-[22px] h-[22px] rounded-full',
                'bg-[var(--color-signal)] text-white',
                'shadow-[0_0_0_4px_var(--color-signal-soft)]',
              ].join(' ')}
              style={
                distance.bearing_degrees != null
                  ? { transform: `rotate(${distance.bearing_degrees}deg)` }
                  : undefined
              }
            >
              <ArrowUp size={12} strokeWidth={3} />
            </span>
            <span className="truncate">
              {formatDistance(distance)}
              {distance.direction ? ` · ${distance.direction}` : ''} · {distance.label}
            </span>
          </div>
        ) : (
          <p className="text-[12.5px] text-[var(--color-muted)] m-0">
            {loading ? 'Loading…' : rows.length > 0 ? 'Result' : 'No result yet.'}
          </p>
        )}
      </div>
      {/* Full panel — always rendered at sm+, also at mobile when
          NOT collapsed. */}
      <div
        className={[
          'px-5 py-4.5 flex flex-col gap-3',
          collapsed ? 'hidden sm:flex' : 'flex',
        ].join(' ')}
      >
      {/* Tiny corner spinner — absolutely positioned so the panel doesn't
          reflow when loading toggles. Pairs with the opacity dim on the
          content wrapper below for a stronger "working" signal that still
          leaves the previous result legible if the user wants to compare. */}
      {loading ? (
        <Loader2
          size={12}
          aria-label="Loading"
          className="absolute right-3 top-3 z-10 animate-spin text-[var(--color-accent)]"
        />
      ) : null}
      {error ? (
        <p className="text-[12.5px] text-[var(--color-signal)]">{error}</p>
      ) : (
        <div
          className={[
            'flex flex-col gap-3',
            // Dim the content while loading — the previous result is still
            // readable, just visibly "stale".
            loading ? 'opacity-60 transition-opacity' : 'transition-opacity',
          ].join(' ')}
        >
          <ul className="list-none m-0 p-0 flex flex-col">
            {rows.map((row, i) => {
              const id = row.geoborder_id ?? null
              const clickable = id != null && onRowClick != null
              const handleHoverIn = id ? () => onRowHover?.(id) : undefined
              const handleHoverOut = id ? () => onRowHover?.(null) : undefined
              const handleClick = clickable ? () => onRowClick(id) : undefined
              const handleKey = clickable
                ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onRowClick(id)
                    }
                  }
                : undefined
              return (
                // The interactive role sits on an inner element, not on the
                // <li>. role="button" replaces the implicit listitem role,
                // which leaves the <ul> directly containing a non-listitem
                // and fails axe's `list` rule. Splitting the two keeps the
                // list semantics and the button semantics both intact.
                <li key={`${row.k}-${i}`}>
                <div
                  onMouseEnter={handleHoverIn}
                  onMouseLeave={handleHoverOut}
                  onFocus={handleHoverIn}
                  // No onBlur handler: clicking the row focuses the <li>,
                  // then the fitBounds animation programmatically shifts
                  // focus, firing blur. Wiring blur to clear hover would
                  // un-highlight the polygon the user just clicked. With
                  // blur unbound, the only clear paths are mouseleave
                  // (cursor moves off the row), hover-on-another-row
                  // (that row's mouseEnter overwrites), and a new
                  // geocode (HeroMap/ExplorePage's lookup() resets).
                  onClick={handleClick}
                  onKeyDown={handleKey}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  aria-label={
                    clickable ? `Zoom to ${row.k}: ${row.v}` : undefined
                  }
                  className={[
                    // py-1 (4px each side) instead of py-1.5 (6px). Saves
                    // ~4px per row × ~10 max-layer rows = ~40px of vertical
                    // height, which on the homepage map clears the
                    // panel-vs-satellite/street-toggle overlap at the top
                    // when City + State + every Local + every Judicial
                    // row are all present. Explore picks the same change
                    // up but the taller map + group-scoped row count means
                    // there's no overlap to fix there.
                    'flex items-center justify-between text-[13px] py-1 px-1.5',
                    '-mx-1.5 rounded-sm transition-colors',
                    id ? 'hover:bg-[var(--color-accent-soft)]' : '',
                    clickable ? 'cursor-pointer' : id ? 'cursor-default' : '',
                    i === 0
                      ? ''
                      : 'border-t border-dashed border-[var(--color-border-soft)]',
                  ].join(' ')}
                >
                  <span className="text-[var(--color-muted)]">{row.k}</span>
                  <span className="font-[var(--font-mono)] tabular-nums">{row.v}</span>
                  </div>
                </li>
              )
            })}
          </ul>
          {landStatus ? <LandStatusRow status={landStatus} /> : null}
          {distance ? (
            <div className="mt-1 flex items-center gap-2.5 font-[var(--font-mono)] text-[13px] tabular-nums">
              <span
                aria-hidden
                className={[
                  'inline-flex items-center justify-center w-[22px] h-[22px] rounded-full',
                  'bg-[var(--color-signal)] text-white',
                  'shadow-[0_0_0_4px_var(--color-signal-soft)]',
                ].join(' ')}
                // Rotate the up-arrow to the bearing (0° = N, CW positive).
                // Null bearing = point exactly on the boundary; render the
                // arrow without rotation as a neutral dot stand-in.
                style={
                  distance.bearing_degrees != null
                    ? { transform: `rotate(${distance.bearing_degrees}deg)` }
                    : undefined
                }
              >
                <ArrowUp size={12} strokeWidth={3} />
              </span>
              {formatDistance(distance)}
              {distance.direction ? ` · ${distance.direction}` : ''} · {distance.label}
            </div>
          ) : null}
        </div>
      )}
      </div>
    </aside>
  )
}
